import { useSocketEvent, useSocketState } from '../helpers/useSocket'
import { countdownRenderer } from '../helpers/countdownRenderer'
import { DraftCiv } from '../components/DraftCiv'
import { DraftMap } from '../components/DraftMap'
import { mergeCivDraftView, mergeMapDraftView } from '../helpers/mergeDraftView'
import React, { useState } from 'react'
import Countdown from 'react-countdown'
import { MatchGame } from '../components/MatchGame'
import { MatchGameDraft } from '../components/MatchGameDraft'
import colors from '../config/colors.json'

export default function DraftDisplay() {
	const [config, setConfig] = useState({ cssTheme: 'default.css' })
	const [match] = useSocketState('Match')
	const [civDraft] = useSocketState('CivDraft')
	const [mapDraft] = useSocketState('MapDraft')
	const [gameCivDraft] = useSocketState('GameCivDraft')
	const [tournament] = useSocketState('Tournament')

	const civDraftView =
		civDraft && match
			? mergeCivDraftView(
					civDraft?.view || {},
					match.games,
					match.matchRedraftAfter,
				)
			: {}
	const mapDraftView =
		mapDraft && match
			? mergeMapDraftView(mapDraft?.view || {}, match.games, 0)
			: {}
	const _gameCivDraftView = gameCivDraft
		? mergeCivDraftView(gameCivDraft?.view || {}, [], 0)
		: {}

	let leftPlayerInfo = tournament?.config?.players?.find((p) =>
		match.leftPlayer.name.toLowerCase().includes(p.name.toLowerCase()),
	)
	let rightPlayerInfo = tournament?.config?.players?.find((p) =>
		match.rightPlayer.name?.toLowerCase().includes(p.name.toLowerCase()),
	)

	useSocketEvent('updateConfig', (newdata) => {
		console.log(`Received updateConfig`, newdata)
		setConfig(newdata)
	})

	return (
		<div
			className="draft"
			style={{
				'--left-player-color':
					colors[match?.leftPlayer?.color]?.mainColor,
				'--left-player-color-secondary':
					colors[match?.leftPlayer?.color]?.secondaryColor,
				'--left-player-color-alpha':
					colors[match?.leftPlayer?.color]?.mainColorAlpha,
				'--left-player-color-alpha7':
					colors[match?.leftPlayer?.color]?.mainColorAlpha7,
				'--right-player-color':
					colors[match?.rightPlayer?.color]?.mainColor,
				'--right-player-color-secondary':
					colors[match?.rightPlayer?.color]?.secondaryColor,
				'--right-player-color-alpha':
					colors[match?.rightPlayer?.color]?.mainColorAlpha,
				'--right-player-color-alpha7':
					colors[match?.rightPlayer?.color]?.mainColorAlpha7,
			}}
		>
			<link
				rel="stylesheet"
				type="text/css"
				href={`/themes/${config.cssTheme}`}
			/>
			{match && (
				<>
					<h1 className="draft__roundInfo">{match.roundInfo}</h1>
					<h1 className="draft__status">{match.matchStatus}</h1>
				</>
			)}
			{match && (
				<>
					<span className="draft__playerInfo draft__playerInfo-left flex flex-row">
						<h1 className="draft__name draft__leftName">
							{match.leftPlayer.name}
						</h1>
						{leftPlayerInfo?.teamPictureUrl && (
							<img
								src={leftPlayerInfo?.teamPictureUrl}
								className="draft__teamIcon draft__leftTeamIcon"
								alt=""
							/>
						)}
					</span>

					<span className="draft__playerInfo draft__playerInfo-right flex flex-row">
						{rightPlayerInfo?.teamPictureUrl && (
							<img
								src={rightPlayerInfo?.teamPictureUrl}
								className="draft__teamIcon draft__rightTeamIcon"
								alt=""
							/>
						)}
						<h1 className="draft__name draft__rightName">
							{match.rightPlayer.name}
						</h1>
					</span>
				</>
			)}
			{match && (
				<>
					<h1 className="draft__leftScore draft__score">
						{match.leftPlayer.score}
					</h1>
					<h1 className="draft__rightScore draft__score">
						{match.rightPlayer.score}
					</h1>
				</>
			)}
			{match && (
				<>
					<div className="draft__leftCountryContainer draft__countryContainer hidden">
						<img
							className="draft__leftCountry draft__country"
							src={`https://flagpedia.net/data/flags/emoji/google/160x160/${match.leftPlayer.country}.png`}
						/>
					</div>
					<div className="draft__rightCountryContainer draft__countryContainer hidden">
						<img
							className="draft__rightCountry draft__country"
							src={`https://flagpedia.net/data/flags/emoji/google/160x160/${match.rightPlayer.country}.png`}
						/>
					</div>
				</>
			)}
			{match && (
				<>
					<h1 className="draft__format">
						{String(match.matchTypeDisplay)}
					</h1>
				</>
			)}
			{match?.caster1 && (
				<div className="draft__caster draft__caster1 hidden">
					<img
						className="draft__caster1Country draft__casterCountry"
						src={`https://flagpedia.net/data/flags/emoji/google/160x160/${match.caster1.country}.png`}
						alt=""
					/>
					<h1 className="draft__caster1Name draft__casterName">
						{match.caster1.name}
					</h1>
				</div>
			)}
			{match?.caster2 && (
				<div className="draft__caster draft__caster2 hidden">
					<img
						className="draft__caster2Country draft__casterCountry"
						src={`https://flagpedia.net/data/flags/emoji/google/160x160/${match.caster2.country}.png`}
						alt=""
					/>
					<h1 className="draft__caster2Name draft__casterName">
						{match.caster2.name}
					</h1>
				</div>
			)}
			{match?.interviewee && (
				<div className="draft__interviewee hidden">
					<img
						className="draft__intervieweeCountry"
						src={`https://flagpedia.net/data/flags/emoji/google/160x160/${match.interviewee.country}.png`}
						alt=""
					/>
					<h1 className="draft__intervieweeName">
						{match.interviewee.name}
					</h1>
				</div>
			)}

			{civDraftView?.host && (
				<div className="draft__civs">
					<div className="draft__left draft__leftAllPicks civ__draft">
						{civDraftView.host.map((draftOption, i) => (
							<DraftCiv
								key={i}
								civ={draftOption.option}
								state={draftOption.action}
								status={draftOption.status}
								randomized={draftOption.randomized}
								result={draftOption.result}
							/>
						))}
					</div>
					<div className="draft__left draft__leftCivPicks civ__draft">
						{civDraftView.host
							.filter(
								(draftOption) => draftOption.action === 'pick',
							)
							.map((draftOption, i) => (
								<DraftCiv
									key={i}
									civ={draftOption.option}
									state={draftOption.action}
									status={draftOption.status}
									randomized={draftOption.randomized}
									result={draftOption.result}
								/>
							))}
					</div>
					<div className="draft__left draft__leftCivBans civ__draft">
						{civDraftView.host
							.filter(
								(draftOption) =>
									draftOption.action === 'ban' ||
									draftOption.action === 'snipe',
							)
							.map((draftOption, i) => (
								<DraftCiv
									key={i}
									civ={draftOption.option}
									state={draftOption.action}
									status={draftOption.status}
									randomized={draftOption.randomized}
									result={draftOption.result}
								/>
							))}
					</div>
					<div className="draft__right draft__rightAllPicks civ__draft">
						{civDraftView.guest.map((draftOption, i) => (
							<DraftCiv
								key={i}
								civ={draftOption.option}
								state={draftOption.action}
								status={draftOption.status}
								randomized={draftOption.randomized}
								result={draftOption.result}
							/>
						))}
					</div>
					<div className="draft__right draft__rightCivPicks civ__draft">
						{civDraftView.guest
							.filter(
								(draftOption) => draftOption.action === 'pick',
							)
							.map((draftOption, i) => (
								<DraftCiv
									key={i}
									civ={draftOption.option}
									state={draftOption.action}
									status={draftOption.status}
									randomized={draftOption.randomized}
									result={draftOption.result}
								/>
							))}
					</div>
					<div className="draft__right draft__rightCivBans civ__draft">
						{civDraftView.guest
							.filter(
								(draftOption) =>
									draftOption.action === 'ban' ||
									draftOption.action === 'snipe',
							)
							.map((draftOption, i) => (
								<DraftCiv
									key={i}
									civ={draftOption.option}
									state={draftOption.action}
									status={draftOption.status}
									randomized={draftOption.randomized}
									result={draftOption.result}
								/>
							))}
					</div>
				</div>
			)}
			{mapDraftView?.host && (
				<>
					<div className="draft__left map__draft">
						{mapDraftView.host.map((draftOption, i) => (
							<DraftMap
								key={i}
								map={draftOption.option}
								state={draftOption.action}
								status={draftOption.status}
								randomized={draftOption.randomized}
								result={draftOption.result}
							/>
						))}
					</div>
					<div className="draft__right map__draft">
						{mapDraftView.guest.map((draftOption, i) => (
							<DraftMap
								key={i}
								map={draftOption.option}
								state={draftOption.action}
								status={draftOption.status}
								randomized={draftOption.randomized}
								result={draftOption.result}
							/>
						))}
					</div>
				</>
			)}
			{match?.countdownTimer && (
				<div className="match__countdown">
					<Countdown
						date={match.countdownTimer}
						renderer={countdownRenderer}
					/>
				</div>
			)}
			{match?.games && (
				<>
					<div className="matchGames">
						{match.games
							.filter((game) => game.state !== 'tbd')
							.map((game, i) => (
								<MatchGame key={i} game={game} />
							))}
					</div>
					<div className="matchGamesDraft hidden">
						{match.games
							.filter((game) => game.state !== 'tbd')
							.map((game, i) => (
								<MatchGameDraft key={i} game={game} />
							))}
					</div>
				</>
			)}
		</div>
	)
}
